import { useState } from "react";
import { useAdmin } from "@/context/AdminContext";
import { storeFormEntry } from "@/utils/formEntryStore";
import { submitFormToSupabase } from "@/utils/submitFormToSupabase";

const GOOGLE_NOTIFY_FORM_ACTION = "https://docs.google.com/forms/d/e/1FAIpQLSc8Amub8mDW3RfCswv4Yt4qiX6x8BfllqvV1MFywQSTlHo3vg/formResponse";
const GOOGLE_NOTIFY_EMAIL_FIELD = "entry.166023422";

interface NotifyBannerProps {
  /** Called when the user dismisses — parent (SiteNotifications) handles the actual dismiss logic */
  onDismiss?: () => void;
}

/** Banner content is now stored in the Popups & Banners system (ma_site_notifications).
 *  This component just renders the email capture UI. Visibility is controlled by the
 *  banner system — NOT by a separate localStorage key. */
export default function NotifyBanner({ onDismiss }: NotifyBannerProps = {}) {
  const { isAdmin } = useAdmin();
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [email, setEmail] = useState("");

  const handleDismiss = () => {
    if (onDismiss) onDismiss();
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!email) return;
    setStatus("submitting");
    const body = new URLSearchParams();
    body.set(GOOGLE_NOTIFY_EMAIL_FIELD, email);
    const notifyFields = [{ label: "Email", value: email }];
    storeFormEntry("notify", "Get Notified (Email List)", notifyFields);
    submitFormToSupabase("notify", "Get Notified (Email List)", notifyFields);

    try {
      await fetch(GOOGLE_NOTIFY_FORM_ACTION, {
        method: "POST",
        mode: "no-cors",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      setStatus("success");
      setEmail("");
    } catch { setStatus("error"); }
  };

  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-[60] w-full"
      style={{ backgroundColor: "#1a1a2e" }}
    >
      {/* Admin hint bar */}
      {isAdmin && (
        <div className="w-full bg-black/30 flex items-center gap-3 px-4 py-1.5 flex-wrap">
          <span className="text-gray-400 text-xs font-semibold">
            <i className="ri-information-line mr-1"></i>
            Visibility controlled by Popups &amp; Banners tab
          </span>
        </div>
      )}

      <div className="max-w-5xl mx-auto px-4 py-3 flex flex-col sm:flex-row items-center gap-3 relative">
        {/* Bell icon + text */}
        <div className="flex items-center gap-2 flex-shrink-0">
          <div className="w-7 h-7 flex items-center justify-center rounded-full bg-pink-500/20">
            <i className="ri-notification-3-line text-pink-400 text-sm"></i>
          </div>
          <span className="text-sm font-semibold text-white whitespace-nowrap">Be the first to know when we open our doors!</span>
        </div>

        {/* Form or success */}
        {status === "success" ? (
          <div className="flex items-center gap-2 sm:ml-auto">
            <i className="ri-check-line text-green-400 text-lg"></i>
            <span className="text-sm font-semibold text-green-300">You're on the list! We'll be in touch.</span>
          </div>
        ) : (
          <form
            onSubmit={handleSubmit}
            id="notify-banner-form"
            className="flex items-center gap-2 sm:ml-auto w-full sm:w-auto"
          >
            <input
              type="email"
              name="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email address"
              required
              className="flex-1 sm:w-60 px-4 py-2 rounded-full text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-pink-400 bg-white/95"
            />
            <button
              type="submit"
              disabled={status === "submitting"}
              className="bg-pink-500 hover:bg-pink-600 text-white font-bold text-sm px-5 py-2 rounded-full whitespace-nowrap cursor-pointer transition-colors"
            >
              {status === "submitting" ? <i className="ri-loader-4-line animate-spin"></i> : "Notify Me"}
            </button>
          </form>
        )}

        {status === "error" && <span className="text-red-300 text-xs">Something went wrong. Try again.</span>}

        {/* X dismiss button — always visible to visitors */}
        <button
          type="button"
          onClick={handleDismiss}
          className="absolute right-4 top-1/2 -translate-y-1/2 w-7 h-7 flex items-center justify-center rounded-full text-gray-400 hover:text-white hover:bg-white/10 transition-colors cursor-pointer flex-shrink-0"
          aria-label="Dismiss notification"
          title="Dismiss"
        >
          <i className="ri-close-line text-base"></i>
        </button>
      </div>
    </div>
  );
}
